<template>
    <div class="parts-player">
        <Spinner v-if="loading">{{ loaderText }}</Spinner>
        <PartsPlayerNav
            :parts="filteredParts"
            :active-part="activePart"
            :page="page"
            :total-pages="totalPages"
            :is-embed="idEmbed"
            :hide-ribbon="isProofreadingReviewer"
            @select="
                makePlaylist({
                    page,
                    partId: $event.partId,
                })
            "
        />

        <div
            class="parts-player__inner"
            :class="{ 'parts-player__inner--reviewer-embed': isProofreadingReviewer }"
        >
            <!-- Professional Prooflistening (embedded outsourced reviewer): dedicated
                 footer matching the exact mock's single-row grouping. -->
            <PartsPlayerFooter
                v-if="isProofreadingReviewer"
                ref="player"
                :src="activePart.src"
                :paused="paused"
                preserve-rate
                @finished="onFinished"
                @playing="playing = true"
                @paused="playing = false"
                @saveRate="onSaveRate"
            >
                <template #extra-controls-before>
                    <button
                        class="parts-player-footer__button"
                        :disabled="!activePart.hasPrev"
                        :class="{ 'parts-player-footer__button--disabled': !activePart.hasPrev }"
                        :aria-label="$t('partsPlayer.prevSentence')"
                        :title="$t('partsPlayer.prevSentence')"
                        @click.prevent="prev(playing)"
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M17.971 4.285A2 2 0 0 1 21 6v12a2 2 0 0 1-3.029 1.715l-9.997-5.998a2 2 0 0 1-.003-3.432z"
                            />
                            <path d="M3 20V4" />
                        </svg>
                    </button>
                </template>
                <template #now-playing>
                    <span class="parts-player-footer__status-label" v-text="playbackStatusLabel" />
                    <div v-if="activePart.type === 'pause'" class="parts-player-footer__current">
                        <span class="parts-player-footer__text">
                            {{ $t('partsPlayer.pause') }}
                        </span>
                        {{ activePart.duration }} {{ $t('partsPlayer.sec') }}
                    </div>
                    <div v-if="activePart.type === 'audio'" class="parts-player-footer__current">
                        <div v-if="activePart.text" class="parts-player-footer__text">
                            <!-- No cutText() truncation here: the reviewer footer's
                                 .parts-player-footer__text/__link already sets
                                 white-space: nowrap + text-overflow: ellipsis
                                 (matching the mock's CSS-driven truncation), so
                                 passing the full sentence lets the ellipsis land
                                 at the actual container edge instead of a fixed
                                 60-char cutoff that's shorter than the mock. -->
                            <a
                                href="#"
                                class="link parts-player-footer__link"
                                @click.prevent="selectPart"
                            >
                                {{ activePart.text }}
                            </a>
                        </div>
                        <span v-else class="parts-player-footer__text">
                            {{ $t('partsPlayer.bump') }}
                        </span>
                    </div>
                </template>
                <template #extra-controls-after>
                    <button
                        class="parts-player-footer__button"
                        :disabled="!activePart.next"
                        :class="{ 'parts-player-footer__button--disabled': !activePart.next }"
                        :aria-label="$t('partsPlayer.nextSentence')"
                        :title="$t('partsPlayer.nextSentence')"
                        @click.prevent="next(playing)"
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M21 4v16" />
                            <path
                                d="M6.029 4.285A2 2 0 0 0 3 6v12a2 2 0 0 0 3.029 1.715l9.997-5.998a2 2 0 0 0 .003-3.432z"
                            />
                        </svg>
                    </button>
                </template>
            </PartsPlayerFooter>

            <!-- Default (non-reviewer-embed) look: unchanged from before this ticket. -->
            <template v-else>
                <div class="parts-player__current-wrapper">
                    <div v-if="activePart.type === 'pause'" class="parts-player__current">
                        <span class="parts-player__text">
                            {{ $t('partsPlayer.pause') }}
                        </span>
                        {{ activePart.duration }} {{ $t('partsPlayer.sec') }}
                    </div>
                    <div v-if="activePart.type === 'audio'" class="parts-player__current">
                        <div v-if="activePart.text" class="parts-player__text">
                            <a href="#" class="link parts-player__link" @click.prevent="selectPart">
                                {{ cutText(activePart.text, 60) }}
                            </a>
                        </div>
                        <span v-else class="parts-player__text">{{ $t('partsPlayer.bump') }}</span>
                    </div>
                </div>
                <Player
                    ref="player"
                    class="parts-player__player"
                    :src="activePart.src"
                    :paused="paused"
                    preserve-rate
                    @finished="onFinished"
                    @playing="playing = true"
                    @paused="playing = false"
                    @saveRate="onSaveRate"
                >
                    <template #extra-controls-before>
                        <button
                            class="button button--blank player__button parts-player__button parts-player__button--prev"
                            :disabled="!activePart.hasPrev"
                            :class="{ 'parts-player__button--disabled': !activePart.hasPrev }"
                            @click.prevent="prev(playing)"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 32 32"
                            >
                                <g fill="#20d0a9">
                                    <path
                                        d="M0 28l12-12.001L0 4zM28 4h4v24h-4zM14 28l12-12.001L14 4z"
                                    />
                                </g>
                            </svg>
                        </button>
                    </template>
                    <template #extra-controls-after>
                        <button
                            class="button button--blank player__button parts-player__button"
                            :disabled="!activePart.next"
                            :class="{
                                'parts-player__button--disabled': !activePart.next,
                            }"
                            @click.prevent="next(playing)"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 32 32"
                            >
                                <g fill="#20d0a9">
                                    <path
                                        d="M0 28l12-12.001L0 4zM28 4h4v24h-4zM14 28l12-12.001L14 4z"
                                    />
                                </g>
                            </svg>
                        </button>
                    </template>
                </Player>
            </template>

            <div class="parts-player__after">
                <slot name="after-player" :play="play" :pause="pause" />
            </div>
        </div>
        <div class="parts-player__preload">
            <audio v-for="a in preloadQueue" :key="a" :src="a" controls preload />
        </div>
    </div>
</template>

<script>
import { PlayerSimple as Player } from '@speechki-frontend/shared/ui/Player';
import cutText from '@speechki-frontend/shared/helpers/functions/cutText';
import { makeid } from '@speechki-frontend/shared/helpers/functions';
import { mapGetters } from 'vuex';

import PartsPlayerNav from './PartsPlayerNav';
import PartsPlayerFooter from './PartsPlayerFooter';
import { saveEditProgress } from '../../services/editProgress';

import { fetchBookpartsPage } from '../../api/bookparts';

const PRELOAD_SIZE = 8;
const PAUSES_URL = process.env.VUE_APP_PAUSES_S3;

const getPauseLength = (duration) => {
    if (duration < 0.15) return duration * 1000;

    let reduced = parseInt(duration * 750);
    return Math.round(reduced) - (Math.round(reduced) % 50);
};

export default {
    name: 'PartsPlayer',
    components: {
        Player,
        PartsPlayerNav,
        PartsPlayerFooter,
    },
    inject: ['selector', 'customerSettings', 'isEmbed'],
    props: {
        totalPages: {
            type: [Number, String],
            default: 0,
        },
    },
    data() {
        return {
            loading: false,
            preloadQueue: [],
            playlist: [],
            activePart: {
                type: '',
                src: '',
            },
            playing: false,
            paused: false,

            parts: [],
            page: 0,
            paginating: false,

            //
            updateUnsub: undefined,

            onlySentence: false,
        };
    },
    computed: {
        loaderText() {
            if (this.paginating) {
                return this.$t('partsPlayer.loadingPage', { number: this.page + 1 });
            }

            return '';
        },
        isLastPage() {
            return this.page === this.totalPages;
        },
        book() {
            return this.$store.state.book.book;
        },
        idEmbed() {
            return location.pathname.includes('/embed/');
        },
        isEditorLight() {
            return location.pathname.includes('/embed/light/');
        },
        ...mapGetters(['isReviewer']),

        isProofreadingReviewer() {
            return this.isEmbed && this.isReviewer;
        },

        playbackStatusLabel() {
            return this.playing ? this.$t('partsPlayer.playing') : this.$t('partsPlayer.paused');
        },

        filteredParts() {
            if (this.customerSettings && this.customerSettings.settings) {
                const { settings } = this.customerSettings;
                const exclude = [];
                if (settings.hideAuthor) {
                    exclude.push('author');
                }
                if (settings.hideTitle) {
                    exclude.push('book_name');
                }

                return this.parts.slice().filter((bp) => {
                    return bp.type_part.slug === 'text' && !exclude.includes(bp.type_text.slug);
                });
            }

            return this.parts;
        },
    },
    watch: {
        activePart() {
            this.$emit('playingPartChanged', { ...this.activePart, page: this.page });
        },

        '$route.query.part'() {
            this.paused = true;
        },

        playing(value) {
            this.paused = !value;
        },
    },
    async created() {
        const { page = 1, part } = this.$route.query;

        this.updateUnsub = this.$store.subscribe(({ type, payload: bookpart }) => {
            if (type.includes('PLAYER_UPDATE_BOOKPART')) {
                this.updateBookpart(bookpart);
            }
        });

        this.fetchUnsub = this.$store.subscribeAction({
            after: ({ type }) => {
                if (!this.page) return;

                if (type === 'pagedBookparts/fetchInitialBookparts') {
                    this.makePlaylist({
                        page: this.page,
                        partId: this.activePart.partId,
                        playImmediately: !!this.playing,
                        forceFetch: true,
                    });
                }
            },
        });

        await this.makePlaylist({ page, partId: +part });
    },
    mounted() {
        document.addEventListener('keydown', this.handleSpacebar);
        this.setRateFromSessionStorage();
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleSpacebar);
        if (this.updateUnsub) {
            this.updateUnsub();
        }
        if (this.fetchUnsub) {
            this.fetchUnsub();
        }
    },
    methods: {
        cutText,
        play() {
            this.$refs.player.play();
        },
        pause() {
            this.$refs.player.pause();
        },
        async makePlaylist({
            page,
            partId,
            playImmediately,
            forceFetch = false,
            onlySentence = false,
        }) {
            if (!page && !partId) {
                console.error('error: no page and part id');
                return;
            }

            this.loading = true;
            this.playlist = [];
            this.preloadQueue = [];
            this.activePart = {};

            let startIndex = 0;

            if (page !== this.page || forceFetch) {
                await this.fetchParts(page);
            }

            if (partId) {
                const partInPageIndex = this.parts.findIndex((part) => part.id === partId);
                if (partInPageIndex > -1) {
                    startIndex = partInPageIndex;
                }
            }

            let partIndex = startIndex;
            const maxPreloadSize =
                startIndex + Math.min(PRELOAD_SIZE, this.parts.length - startIndex);

            for (partIndex; partIndex < maxPreloadSize; partIndex++) {
                const part = this.parts[partIndex];

                if (partIndex === 0 && this.isPause(part)) {
                    continue;
                }
                this.parsePart(part, true, partIndex);
            }

            this.activePart = this.playlist[0] || {};

            if (this.activePart.type !== 'pause') {
                saveEditProgress({
                    bookId: this.book.id,
                    part: this.activePart.partId,
                    page: this.page,
                });
            }

            this.$emit('playingPartChanged', { ...this.activePart, page: this.page });

            this.loading = false;
            if (playImmediately) {
                await this.$nextTick();
                this.$refs.player.play();

                if (onlySentence) {
                    this.onlySentence = true;
                }
            }
        },

        isPause(part) {
            return part.type_part.slug === 'pause' || part.type_part.slug === 'paragraph_end';
        },

        parsePart(part, persistPreload, partIndex) {
            try {
                const {
                    type_part: { slug },
                } = part;

                const isLastInPage = partIndex === this.parts.length - 1;
                switch (slug) {
                    case 'pause':
                    case 'paragraph_end':
                        if (part.pause === 0) {
                            break;
                        }
                        this.pushPreload(this.getPauseSrc(part.pause), persistPreload);
                        this.addPauseToPlaylist(
                            {
                                duration: part.pause,
                                partId: part.id,
                                partIndex,
                            },
                            isLastInPage
                        );
                        break;

                    case 'audio':
                        this.pushPreload(part.pre_recorded_audio.audio, persistPreload);
                        this.addBumpToPlaylist(
                            {
                                src: part.pre_recorded_audio.audio,
                                partIndex,
                                partId: part.id,
                                duration: part.pre_recorded_audio.duration,
                            },
                            isLastInPage
                        );
                        break;

                    case 'text':
                        this.pushPreload(part.audio, persistPreload);
                        this.addAudioToPlaylist(
                            {
                                src: part.audio,
                                partId: part.id,
                                partIndex,
                                localId: part.local_id,
                                text: part.raw_text,
                                duration: part.audio_duration,
                            },
                            isLastInPage
                        );
                        break;
                }
            } catch (e) {
                console.log(e);
            }
        },

        // @TODO i might not need 3 methods and switch here
        addPauseToPlaylist({ duration, partId, partIndex, localId = null }, isLast = false) {
            const last = this.getLastPlaylistItem() || {};
            const lastNext = (last && last.next) || 0;

            this.playlist.push({
                id: makeid(),
                type: 'pause',
                src: this.getPauseSrc(duration),
                duration,
                partId,
                partIndex,
                localId,
                hasPrev: this.hasPrev(partIndex),
                next: isLast ? null : lastNext + 1,
            });
        },
        addAudioToPlaylist(
            { src, partId, localId = null, text, partIndex, duration },
            isLast = false
        ) {
            const last = this.getLastPlaylistItem() || {};
            const lastNext = (last && last.next) || 0;

            this.playlist.push({
                id: makeid(),
                src,
                type: 'audio',
                partId,
                partIndex,
                localId,
                text,
                hasPrev: this.hasPrev(partIndex),
                next: isLast ? null : lastNext + 1,
                duration,
            });
        },
        addBumpToPlaylist({ src, partId, partIndex, duration }, isLast = false) {
            const last = this.getLastPlaylistItem() || {};
            const lastNext = (last && last.next) || 0;

            this.playlist.push({
                id: makeid(),
                src,
                type: 'bump',
                partId,
                partIndex,
                hasPrev: this.hasPrev(partIndex),
                next: isLast ? null : lastNext + 1,
                duration,
            });
        },

        hasPrev(partIndex) {
            return (
                (partIndex === 1 &&
                    this.parts[0].type_part.slug !== 'pause' &&
                    this.parts[0].type_part.slug !== 'bump') ||
                partIndex > 1
            );
        },
        getLastPlaylistItem() {
            return this.playlist.length ? this.playlist[this.playlist.length - 1] : null;
        },
        getPauseSrc(duration) {
            const prefix = this.book.pause_prefix || '';

            return `${PAUSES_URL}${prefix}${getPauseLength(duration)}.wav`;
        },
        async onFinished() {
            if (this.onlySentence) {
                this.onlySentence = false;
                return;
            }

            if (this.activePart.next) {
                this.next();
                return;
            }

            if (this.isLastPage) {
                return;
            }

            await this.loadNextPage();
        },
        async loadNextPage() {
            this.preloadQueue = [];
            this.paginating = true;
            await this.makePlaylist({ page: this.page + 1 });
            this.$nextTick(() => {
                this.$refs.player.play();
            });
            this.paginating = false;
        },
        next(play = true) {
            if (!this.activePart.next) {
                return;
            }

            this.activePart = this.playlist[this.activePart.next];

            if (!this.activePart.duration) {
                this.next();
            }

            const lastPlaylistItem = this.getLastPlaylistItem();
            this.preloadNext(lastPlaylistItem.partIndex);

            if (this.activePart.type !== 'pause') {
                saveEditProgress({
                    bookId: this.book.id,
                    part: this.activePart.partId,
                    page: this.page,
                });
            }

            if (play) {
                this.$nextTick(() => {
                    this.$emit('playingNextPart', { ...this.activePart });
                    this.$refs.player.play();
                });
            }
        },

        preloadNext(lastPartIndex) {
            if (lastPartIndex === this.parts.length - 1) {
                return;
            }

            const part = this.parts[lastPartIndex + 1];

            this.parsePart(part, false, lastPartIndex + 1);
        },

        prev(play = false) {
            if (!this.activePart.hasPrev) return;

            const part = this.parts[this.activePart.partIndex - 1];

            this.makePlaylist({
                page: this.page,
                partId: part.id,
                playImmediately: play,
            });
        },

        pushPreload(audio, persist) {
            if (!persist && this.preloadQueue.length) {
                this.preloadQueue.shift();
            }

            if (this.preloadQueue.indexOf(audio) === -1) {
                this.preloadQueue.push(audio);
            }
        },

        selectPart() {
            if (!this.activePart || !this.selector) return;

            let payload = {
                id: this.activePart.partId,
            };

            this.selector.select(payload);
        },

        async fetchParts(page) {
            try {
                this.loading = true;
                this.parts = [];

                const { results } = await fetchBookpartsPage(this.$route.params.id, page);

                this.page = Number(page);
                this.parts = results;
            } catch (error) {
                console.log(error);
            } finally {
                this.loading = false;
            }
        },

        updateBookpart(bookpart) {
            const index = this.parts.findIndex((bp) => bp.id === bookpart.id);
            if (index === -1) return;

            this.parts[index] = bookpart;
            this.makePlaylist({
                page: this.page,
                partId: this.activePart.partId,
                playImmediately: this.playing,
            });
        },

        handleSpacebar(event) {
            // @todo remove this hothackfix
            if (
                event.code === 'Space' &&
                event.target == document.body &&
                !!this.$refs.player &&
                !document.querySelector('.audio-editor')
            ) {
                event.preventDefault();

                this.playing = !this.playing;
                this.$refs.player.toggle();
            }
        },
        onSaveRate(rate) {
            sessionStorage.setItem('playerRate', rate);
        },
        setRateFromSessionStorage() {
            const rate = sessionStorage.getItem('playerRate');
            if (!rate) return;
            this.$refs.player.setRate(rate);
        },
    },
};
</script>

<style lang="scss" src="./PartsPlayer.scss"></style>
